import { useState } from 'react'
import { useMutation } from 'react-query'
import * as Sentry from '@sentry/react'
import Cookies from 'js-cookie'
import { postConsumerOrder, postSmbOrder } from '../api'
import { CONSUMER_CART_SOURCE } from '../constants'

const useCreateOrder = (cartSource) => {
    const [errorMessage, setErrorMessage] = useState('')
    const [errorCode, setErrorCode] = useState('')

    const { mutate, data, error, isLoading, isSuccess, isError, reset } =
        useMutation(
            cartSource === CONSUMER_CART_SOURCE
                ? postConsumerOrder
                : postSmbOrder,
            {
                retry: (failureCount, error) =>
                    failureCount === 0 && error.response.status === 504, // Retry once after 504 only
                onError: (error, context) => {
                    const {
                        response: { data: errorData, status },
                    } = error
                    console.log("DATA", errorData, errorData?.canSendSms === false)
                    if(errorData?.canSendSms === false){
                        const defaultErrorMessage =
                            'We are unable to send a text to this number. Landline, VoIP, and International numbers are not supported. Please enter a valid phone number.'
                        setErrorMessage(defaultErrorMessage)
                        error.name = `Error creating ${cartSource} order, phone did not pass deliverablity checks`
                        Sentry.captureException(error, {
                            level: Sentry.Severity.Error,
                            extra: {
                                errorCode: JSON.stringify(
                                    errorData?.error_code
                                ),
                                errorMessage: JSON.stringify(
                                    errorData?.message || defaultErrorMessage
                                ),
                                context: JSON.stringify(context, null, 2),
                            },
                            user: {
                                id: JSON.stringify(
                                    Cookies.get('GCG_CLIENT_USER_ID')
                                ),
                            },
                        })
                    }
                    if(errorData?.success === false && errorData?.errorMessage === 'duplicate_phone'){
                        const defaultErrorMessage =
                            'Whoops! That phone number is already in use by another customer. Please, try using a different number.'
                        setErrorMessage(defaultErrorMessage)
                        error.name = `Error creating ${cartSource} order, phone is used by another user`
                        Sentry.captureException(error, {
                            level: Sentry.Severity.Error,
                            extra: {
                                errorCode: JSON.stringify(
                                    errorData?.error_code
                                ),
                                errorMessage: JSON.stringify(
                                    errorData?.message || defaultErrorMessage
                                ),
                                context: JSON.stringify(context, null, 2),
                            },
                            user: {
                                id: JSON.stringify(
                                    Cookies.get('GCG_CLIENT_USER_ID')
                                ),
                            },
                        })
                    }
                    if(errorData?.success === false && errorData?.errorMessage === 'no_phone'){
                        const defaultErrorMessage =
                            'Whoops! A phone number is required for this order.'
                        setErrorMessage(defaultErrorMessage)
                        error.name = `Error creating ${cartSource} order, phone number is required for this order`
                        Sentry.captureException(error, {
                            level: Sentry.Severity.Error,
                            extra: {
                                errorCode: JSON.stringify(
                                    errorData?.error_code
                                ),
                                errorMessage: JSON.stringify(
                                    errorData?.message || defaultErrorMessage
                                ),
                                context: JSON.stringify(context, null, 2),
                            },
                            user: {
                                id: JSON.stringify(
                                    Cookies.get('GCG_CLIENT_USER_ID')
                                ),
                            },
                        })
                    }
                    if (status === 500) {
                        const defaultErrorMessage =
                            'There was an error creating your order. Please refresh the page and try again.'
                        setErrorMessage(defaultErrorMessage)
                        error.name = `Error creating ${cartSource} order`
                        Sentry.captureException(error, {
                            level: Sentry.Severity.Error,
                            extra: {
                                errorCode: JSON.stringify(
                                    errorData?.error_code
                                ),
                                errorMessage: JSON.stringify(
                                    errorData?.message || defaultErrorMessage
                                ),
                                context: JSON.stringify(context, null, 2),
                            },
                            user: {
                                id: JSON.stringify(
                                    Cookies.get('GCG_CLIENT_USER_ID')
                                ),
                            },
                        })
                    } else if (errorData?.error_code) {
                        setErrorCode(errorData.error_code)
                        setErrorMessage(`${errorData.message}`)
                        error.name = `Error creating ${cartSource} order - ${errorData.message}`
                        Sentry.captureException(error, {
                            level: Sentry.Severity.Info,
                            extra: {
                                errorCode: JSON.stringify(errorData.error_code),
                                errorMessage: JSON.stringify(errorData.message),
                                context: JSON.stringify(context, null, 2),
                            },
                            user: {
                                id: JSON.stringify(
                                    Cookies.get('GCG_CLIENT_USER_ID')
                                ),
                            },
                        })
                    } else if (errorData?.errors) {
                        setErrorMessage(`${errorData.errors[0]}`)
                        error.name = `Error creating ${cartSource} order - ${errorData.errors[0]}`
                        Sentry.captureException(error, {
                            level: Sentry.Severity.Info,
                            extra: {
                                errorCode: JSON.stringify(errorData.error_code),
                                errorMessage: JSON.stringify(
                                    errorData.errors[0]
                                ),
                                context: JSON.stringify(context, null, 2),
                            },
                            user: {
                                id: JSON.stringify(
                                    Cookies.get('GCG_CLIENT_USER_ID')
                                ),
                            },
                        })
                    }
                },
            }
        )

    return {
        mutate,
        data,
        error,
        isLoading,
        isSuccess,
        isError,
        errorMessage,
        reset,
        errorCode,
    }
}

export default useCreateOrder
